import { Controller } from "@hotwired/stimulus";
import { Turbo } from "@hotwired/turbo-rails";

export default class UserSearchController extends Controller {
  static targets = ["form", "skeleton", "results", "error"];

  static values = {
    delay: { type: Number, default: 200 },
    pageParam: { type: String, default: "page" },
    refreshOnRestore: { type: Boolean, default: false },
  };

  connect() {
    if (!this.refreshOnRestoreValue) return;

    this.profileClick = (event) => {
      const link = event.target.closest("a[data-follow-refresh-url]");
      if (link) this.element.dataset.followRefreshUrl = link.dataset.followRefreshUrl;
    };
    this.pageShow = (event) => {
      if (event.persisted) this.refresh();
    };
    this.element.addEventListener("click", this.profileClick);
    window.addEventListener("pageshow", this.pageShow);

    this.refresh();
  }

  async refresh() {
    const url = this.element.dataset.followRefreshUrl;
    if (!url || this.followRequest) return;

    const request = new AbortController();
    this.followRequest = request;
    try {
      const response = await fetch(url, {
        headers: { Accept: "text/vnd.turbo-stream.html" },
        cache: "no-store",
        signal: request.signal,
      });
      if (!response.ok) return;
      const html = await response.text();
      if (this.followRequest !== request) return;
      Turbo.renderStreamMessage(html);
      if (this.element.dataset.followRefreshUrl === url) {
        delete this.element.dataset.followRefreshUrl;
      }
    } catch (error) {
      if (error.name !== "AbortError") console.error("Unable to refresh follow button:", error);
    } finally {
      if (this.followRequest === request) this.followRequest = null;
    }
  }

  typing() {
    this.cancel();
    this.setLoading(true);
    this.timer = setTimeout(() => this.search(), this.delayValue);
  }

  submit(event) {
    event.preventDefault();
    this.cancel();
    this.setLoading(true);
    this.search();
  }

  cancel() {
    clearTimeout(this.timer);
    this.request?.abort();
    this.request = null;
  }

  setLoading(loading) {
    this.skeletonTarget.classList.toggle("d-none", !loading);
    this.resultsTarget.classList.toggle("d-none", loading);
    this.resultsTarget.setAttribute("aria-busy", String(loading));
    this.errorTarget.classList.add("d-none");
  }

  async search() {
    const request = new AbortController();
    this.request = request;
    const url = new URL(this.formTarget.action);
    url.search = new URLSearchParams(new FormData(this.formTarget)).toString();
    url.searchParams.delete(this.pageParamValue);

    try {
      const response = await fetch(url, {
        headers: { Accept: "text/vnd.turbo-stream.html" },
        signal: request.signal,
      });
      if (!response.ok) throw new Error("Search failed");
      const html = await response.text();
      if (this.request !== request) return;
      Turbo.renderStreamMessage(html);
      // Turbo renders streams on the next animation frame.
      await new Promise((resolve) => requestAnimationFrame(resolve));
      if (this.request === request) this.setLoading(false);
    } catch (error) {
      if (this.request !== request || error.name === "AbortError") return;
      this.setLoading(false);
      this.errorTarget.classList.remove("d-none");
    }
  }

  disconnect() {
    this.cancel();
    this.followRequest?.abort();
    this.followRequest = null;
    this.element.removeEventListener("click", this.profileClick);
    window.removeEventListener("pageshow", this.pageShow);
  }
}
